// =============================================================================
// Renovação manual de um contrato (migration 050)
//
// Um contrato é centro de custo + categoria + subcategoria (opcional). O aviso
// de 60, 30 e 2 dias (049, 062) sai desta data; informar a data do ciclo seguinte fecha
// o aviso do ciclo que passou. Aberto pela Planilha (em cada linha e
// subcategoria) e pela Central (no próprio aviso).
// =============================================================================

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type FalhaApi } from "../lib/api.ts";
import { Modal } from "../componentes/Modal.tsx";

export type AlvoRenovacao = {
  centro_custo_id: string;
  categoria_id: string;
  subcategoria: string | null;
  /** Como a tela mostra o contrato: "200 Administrativo · Softwares › Slack". */
  rotulo: string;
  proxima_renovacao?: string | null;
  observacao?: string | null;
  /** Já existe no cadastro de renovações (050). Aviso que veio da LINHA (049)
   *  ainda não está no cadastro: salvar cria, e não há o que tirar. */
  registrado: boolean;
};

/** dd/mm/aaaa, sem passar por Date: a data é de calendário, não instante. */
export function dataRenovacao(d: string): string {
  const [ano, mes, dia] = d.split("-");
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : d;
}

type Resultado = { mudou?: boolean; removida?: boolean; alertas?: { abertas_60: number; abertas_30: number; abertas_2?: number } };

const rotuloCampo = "text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase";
const campo = "mt-1 w-full rounded-lg border border-linha bg-superficie px-2.5 py-2 text-[13px]";

/** Hoje em São Paulo, no formato do <input type="date">. */
function hojeSP(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());
}

export function ModalRenovacao({ alvo, onFechar }: { alvo: AlvoRenovacao; onFechar: () => void }) {
  const qc = useQueryClient();
  const existente = alvo.registrado;
  const [data, setData] = useState(alvo.proxima_renovacao ?? "");
  const [obs, setObs] = useState(alvo.observacao ?? "");
  const hoje = hojeSP();

  const salvar = useMutation({
    mutationFn: (remover: boolean) =>
      api.post<Resultado>("/api/renovacoes", {
        centro_custo_id: alvo.centro_custo_id,
        categoria_id: alvo.categoria_id,
        subcategoria: alvo.subcategoria,
        proxima_renovacao: remover ? null : data,
        observacao: obs.trim() || null,
      }),
    onSuccess: () => {
      // A planilha mostra a data; a Central, o sino e os contadores mostram o
      // aviso que a RPC acabou de abrir ou fechar.
      void qc.invalidateQueries({
        predicate: (x) => ["planilha", "excecoes", "contadores", "avisos"].includes(String(x.queryKey[0])),
      });
      onFechar();
    },
  });

  const dataValida = /^\d{4}-\d{2}-\d{2}$/.test(data) && data >= hoje;

  return (
    <Modal titulo="Renovação do contrato" onFechar={onFechar}>
      <div className="space-y-4">
        <p className="text-[13px] font-semibold text-tinta">{alvo.rotulo}</p>
        <p className="text-[13px] leading-snug text-tinta-media">
          Contrato com <strong className="font-semibold">renovação manual</strong>: o sistema avisa na Central e no
          sino aos 60 dias, aos 30 e a 2 dias da data. Ao renovar, informe aqui a data do ciclo seguinte: o aviso do ciclo
          que passou sai sozinho.
        </p>
        <label className="block">
          <span className={rotuloCampo}>Próxima renovação</span>
          <input type="date" value={data} min={hoje} onChange={(e) => setData(e.target.value)} className={campo} />
        </label>
        <label className="block">
          <span className={rotuloCampo}>Observação (opcional)</span>
          <input type="text" value={obs} maxLength={200} onChange={(e) => setObs(e.target.value)}
            placeholder="Ex.: plano anual, renovar com 30 dias de aviso ao fornecedor" className={campo} />
        </label>

        {salvar.isError && (
          <p className="rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
            {(salvar.error as FalhaApi).message}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {existente && (
            <button type="button" disabled={salvar.isPending} onClick={() => salvar.mutate(true)}
              title="O contrato deixa de ter renovação manual e para de avisar"
              className="rounded-lg border border-linha px-3 py-2 text-[13px] text-risco-tinta hover:bg-risco-bg disabled:opacity-45">
              Tirar a renovação manual
            </button>
          )}
          <span className="ml-auto flex gap-2">
            <button type="button" onClick={onFechar}
              className="rounded-lg border border-linha px-4 py-2 text-[13px] hover:bg-superficie-2">
              Cancelar
            </button>
            <button type="button" disabled={!dataValida || salvar.isPending} onClick={() => salvar.mutate(false)}
              className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
              {salvar.isPending ? "Salvando…" : "Salvar"}
            </button>
          </span>
        </div>
      </div>
    </Modal>
  );
}
